import { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Store, Inbox, Loader2, X, MapPin, ExternalLink,
  ClipboardList, BarChart3, Download, FileText, DollarSign, Package, Users, TrendingUp,
  Route, Navigation, Bell, Send, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hpg_admin_token";

const emptyRetailer = { name: "", address: "", city: "", state: "", zip: "", phone: "", poc_name: "", email: "" };

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!token);
  const [authed, setAuthed] = useState(false);

  const verify = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error();
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (token) verify(token);
  }, [token, verify]);

  const onLogin = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setChecking(true);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-blaze" size={28} />
      </div>
    );
  }

  if (!authed) return <Login onLogin={onLogin} />;
  return <Dashboard token={token} onLogout={onLogout} />;
}

/* ---------------- Login ---------------- */
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      onLogin(data.access_token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-foreground font-body flex items-center justify-center px-6">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="grain-overlay" aria-hidden="true" />
      <div className="ember-glow absolute inset-0 pointer-events-none opacity-40" aria-hidden="true" />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative w-full max-w-md border border-surface-border bg-surface/80 p-8 sm:p-10">
        <img src="/brand/logo.jpeg" alt="Hot Packz Grabba" className="w-36 mx-auto mb-8" />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-blaze mb-2 text-center">Admin Console</p>
        <h1 className="font-display uppercase text-3xl text-center mb-8">Sign In</h1>
        <label className="block mb-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Email</span>
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="info@hotpackzgrabba.com" />
        </label>
        <label className="block mb-8">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Password</span>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
        </label>
        <button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-blaze text-white font-heading uppercase text-sm tracking-wider py-4 hover:bg-gold hover:text-ink transition-colors duration-200 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={18} /> : null}
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("overview");
  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  return (
    <div className="min-h-screen bg-ink text-foreground font-body">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="grain-overlay" aria-hidden="true" />
      <header className="border-b border-surface-border sticky top-0 bg-ink/90 backdrop-blur-md z-40">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo.jpeg" alt="Hot Packz Grabba" className="h-9 w-9 object-cover" />
            <span className="font-heading uppercase text-sm tracking-widest">Admin <span className="text-fire">Console</span></span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-gold transition-colors">
              <ExternalLink size={14} /> View Site
            </a>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 border border-surface-border px-4 py-2 font-heading uppercase text-xs tracking-wider hover:border-blaze hover:text-blaze transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap gap-px bg-surface-border border border-surface-border w-fit mb-8">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} icon={BarChart3} label="Overview" testid="tab-overview" />
          <TabBtn active={tab === "preorders"} onClick={() => setTab("preorders")} icon={ClipboardList} label="Preorders" testid="tab-preorders" />
          <TabBtn active={tab === "routes"} onClick={() => setTab("routes")} icon={Route} label="Routes" testid="tab-routes" />
          <TabBtn active={tab === "reminders"} onClick={() => setTab("reminders")} icon={Bell} label="Reminders" testid="tab-reminders" />
          <TabBtn active={tab === "retailers"} onClick={() => setTab("retailers")} icon={Store} label="Retailers" testid="tab-retailers" />
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={Inbox} label="Messages" testid="tab-messages" />
        </div>

        {tab === "overview" && <Overview authHeaders={authHeaders} />}
        {tab === "preorders" && <Preorders authHeaders={authHeaders} token={token} />}
        {tab === "routes" && <Routes authHeaders={authHeaders} />}
        {tab === "reminders" && <Reminders authHeaders={authHeaders} />}
        {tab === "retailers" && <Retailers authHeaders={authHeaders} />}
        {tab === "messages" && <Messages authHeaders={authHeaders} />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-2 px-6 py-3 font-heading uppercase text-xs tracking-wider transition-colors ${active ? "bg-blaze text-white" : "bg-ink text-muted-foreground hover:text-white"}`}>
      <Icon size={15} /> {label}
    </button>
  );
}

/* ---------------- Retailers tab ---------------- */
function Retailers({ authHeaders }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // retailer obj or 'new'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/retailers`, { headers: authHeaders });
      setList(await res.json());
    } catch {
      toast.error("Failed to load retailers");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}" (${r.address})?`)) return;
    try {
      const res = await fetch(`${API}/admin/retailers/${r.id}`, { method: "DELETE", headers: authHeaders });
      if (!res.ok) throw new Error();
      toast.success("Retailer deleted");
      setList((l) => l.filter((x) => x.id !== r.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const save = async (form) => {
    const isNew = !form.id;
    const url = isNew ? `${API}/admin/retailers` : `${API}/admin/retailers/${form.id}`;
    const { id, ...body } = form;
    const res = await fetch(url, { method: isNew ? "POST" : "PUT", headers: authHeaders, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Save failed");
    toast.success(isNew ? "Retailer added" : "Retailer updated");
    setEditing(null);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display uppercase text-3xl leading-none">Retailers</h2>
          <p className="text-muted-foreground text-sm mt-1">{list.length} locations · updates go live instantly</p>
        </div>
        <button data-testid="add-retailer-btn" onClick={() => setEditing({ ...emptyRetailer })} className="inline-flex items-center gap-2 bg-blaze text-white font-heading uppercase text-xs tracking-wider py-3 px-5 hover:bg-gold hover:text-ink transition-colors">
          <Plus size={16} /> Add Retailer
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 p-12 text-muted-foreground border border-surface-border"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : (
        <div data-testid="retailer-list" className="space-y-px bg-surface-border border border-surface-border">
          {list.map((r) => (
            <div key={r.id} data-testid={`retailer-row-${r.id}`} className="bg-ink p-5 flex items-start justify-between gap-4 hover:bg-surface transition-colors">
              <div className="flex items-start gap-4 min-w-0">
                <MapPin className="shrink-0 mt-1 text-blaze" size={18} />
                <div className="min-w-0">
                  <h3 className="font-heading uppercase text-sm tracking-tight truncate">{r.name}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{r.address}, {r.city}, {r.state} {r.zip}</p>
                  {(r.poc_name || r.email) && (
                    <p className="text-sm mt-1.5">
                      {r.poc_name && <span className="text-white" data-testid={`retailer-poc-${r.id}`}>{r.poc_name}</span>}
                      {r.poc_name && r.email && <span className="text-muted-foreground"> · </span>}
                      {r.email && <a href={`mailto:${r.email}`} data-testid={`retailer-email-${r.id}`} className="text-blaze hover:text-gold">{r.email}</a>}
                    </p>
                  )}
                  {r.phone && <p className="text-gold text-xs mt-1 font-mono">{r.phone}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`edit-${r.id}`} onClick={() => setEditing(r)} className="p-2 border border-surface-border hover:border-gold hover:text-gold transition-colors" aria-label="Edit"><Pencil size={15} /></button>
                <button data-testid={`delete-${r.id}`} onClick={() => remove(r)} className="p-2 border border-surface-border hover:border-blaze hover:text-blaze transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <RetailerModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function RetailerModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({ ...emptyRetailer, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.city || !form.state) {
      toast.error("Name, address, city and state are required.");
      return;
    }
    setBusy(true);
    try { await onSave(form); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 backdrop-blur-sm px-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="retailer-modal" className="w-full max-w-lg border border-surface-border bg-surface p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display uppercase text-2xl">{form.id ? "Edit" : "Add"} Retailer</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-white" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <Field label="Business Name *"><input data-testid="field-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Shop name" /></Field>
          <Field label="Address *"><input data-testid="field-address" value={form.address} onChange={set("address")} className={inputCls} placeholder="Street address" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="City *"><input data-testid="field-city" value={form.city} onChange={set("city")} className={inputCls} /></Field>
            <Field label="State *"><input data-testid="field-state" value={form.state} onChange={set("state")} className={inputCls} placeholder="NC" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="ZIP"><input data-testid="field-zip" value={form.zip} onChange={set("zip")} className={inputCls} /></Field>
            <Field label="Phone"><input data-testid="field-phone" value={form.phone || ""} onChange={set("phone")} className={inputCls} placeholder="(optional)" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Point of Contact"><input data-testid="field-poc" value={form.poc_name || ""} onChange={set("poc_name")} className={inputCls} placeholder="(optional)" /></Field>
            <Field label="Email"><input data-testid="field-email" type="email" value={form.email || ""} onChange={set("email")} className={inputCls} placeholder="(optional)" /></Field>
          </div>
        </div>
        <div className="flex gap-3 mt-8">
          <button data-testid="save-retailer" type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-blaze text-white font-heading uppercase text-sm py-3.5 hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} Save
          </button>
          <button type="button" onClick={onClose} className="px-6 border border-surface-border font-heading uppercase text-sm hover:border-white transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- Messages tab ---------------- */
function Messages({ authHeaders }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/contacts`, { headers: authHeaders });
        setList(await res.json());
      } catch {
        toast.error("Failed to load messages");
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div>
      <h2 className="font-display uppercase text-3xl leading-none mb-1">Messages</h2>
      <p className="text-muted-foreground text-sm mb-6">{list.length} contact form submissions</p>
      {loading ? (
        <div className="flex items-center gap-3 p-12 text-muted-foreground border border-surface-border"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : list.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground border border-surface-border">No messages yet.</div>
      ) : (
        <div data-testid="messages-list" className="space-y-px bg-surface-border border border-surface-border">
          {list.map((m) => (
            <div key={m.id} data-testid={`message-${m.id}`} className="bg-ink p-6 hover:bg-surface transition-colors">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="font-heading uppercase text-sm">{m.name}</span>
                <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${m.inquiry_type === "retailer" ? "border-gold text-gold" : "border-surface-border text-muted-foreground"}`}>{m.inquiry_type}</span>
                <span className="text-muted-foreground/60 text-xs ml-auto">{fmt(m.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground mb-3 font-mono">
                <a href={`mailto:${m.email}`} className="text-blaze hover:text-gold">{m.email}</a>
                {m.phone && <span>{m.phone}</span>}
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Overview tab ---------------- */
function Overview({ authHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("month"); // "month" | "all"

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/analytics`, { headers: authHeaders });
        setData(await res.json());
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="flex items-center gap-3 p-12 text-muted-foreground border border-surface-border"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
  if (!data) return null;

  const maxRev = Math.max(...data.monthly_revenue.map((m) => m.revenue), 1);
  const allOrders = Object.values(data.status_counts).reduce((s, n) => s + n, 0);
  const isMonth = scope === "month";
  const monthLabel = (() => {
    const cm = data.current_month || "";
    const [y, m] = cm.split("-");
    if (!y || !m) return "This month";
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
  })();
  const revenueVal = isMonth ? data.month_revenue : data.total_revenue;
  const unitsVal = isMonth ? data.month_units_delivered : data.total_units_delivered;
  const ordersVal = isMonth ? data.month_orders_completed : data.status_counts.completed;

  return (
    <div data-testid="overview-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display uppercase text-3xl leading-none mb-1">Overview</h2>
          <p className="text-muted-foreground text-sm">Live business snapshot</p>
        </div>
        <div className="inline-flex border border-surface-border bg-ink" data-testid="scope-toggle">
          <button
            data-testid="scope-month"
            onClick={() => setScope("month")}
            className={`font-heading uppercase text-xs tracking-wider px-4 py-2 transition-colors duration-200 ${isMonth ? "bg-blaze text-white" : "text-muted-foreground hover:text-white"}`}
          >
            This Month
          </button>
          <button
            data-testid="scope-all"
            onClick={() => setScope("all")}
            className={`font-heading uppercase text-xs tracking-wider px-4 py-2 transition-colors duration-200 ${!isMonth ? "bg-blaze text-white" : "text-muted-foreground hover:text-white"}`}
          >
            All Time
          </button>
        </div>
      </div>

      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold mb-3" data-testid="scope-label">
        {isMonth ? monthLabel : "All time"}
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-surface-border border border-surface-border mb-8">
        <Stat icon={DollarSign} label={isMonth ? "Revenue (this month)" : "Revenue (all time)"} value={money(revenueVal)} testid="stat-revenue" accent />
        <Stat icon={TrendingUp} label="Pending (pipeline)" value={money(data.pending_revenue)} testid="stat-pending" />
        <Stat icon={Package} label={isMonth ? "Cases (this month)" : "Cases (all time)"} value={unitsVal} testid="stat-units" />
        <Stat icon={Store} label="Total stores" value={data.total_stores} testid="stat-stores" />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Orders + status */}
        <div className="border border-surface-border p-6">
          <h3 className="font-heading uppercase text-sm tracking-wider mb-4">Orders</h3>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <MiniStat label="Total orders" value={data.total_orders} />
            <MiniStat label="Orders this month" value={data.orders_this_month} />
            <MiniStat label={isMonth ? "Delivered (month)" : "Delivered (all)"} value={ordersVal} />
          </div>
          <div className="space-y-2">
            <StatusBar label="Pending" value={data.status_counts.pending} total={allOrders} color="bg-gold" />
            <StatusBar label="Completed" value={data.status_counts.completed} total={allOrders} color="bg-green-500" />
            <StatusBar label="Void" value={data.status_counts.void} total={allOrders} color="bg-blaze" />
          </div>
        </div>

        {/* Monthly revenue */}
        <div className="border border-surface-border p-6">
          <h3 className="font-heading uppercase text-sm tracking-wider mb-4">Revenue · last 6 months</h3>
          <div className="flex items-end justify-between gap-2 h-40">
            {data.monthly_revenue.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full gap-2">
                <span className="font-mono text-[10px] text-gold">{m.revenue > 0 ? `$${Math.round(m.revenue)}` : ""}</span>
                <div className="w-full bg-blaze/80 transition-all" style={{ height: `${(m.revenue / maxRev) * 100}%`, minHeight: m.revenue > 0 ? "4px" : "0" }} />
                <span className="font-mono text-[9px] text-muted-foreground uppercase">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top customers */}
      <div className="border border-surface-border p-6 mt-8">
        <h3 className="font-heading uppercase text-sm tracking-wider mb-4 flex items-center gap-2"><Users size={16} /> Biggest customers</h3>
        {data.top_customers.length === 0 ? (
          <p className="text-muted-foreground text-sm">No delivered orders yet.</p>
        ) : (
          <div className="space-y-px bg-surface-border border border-surface-border">
            {data.top_customers.map((c, i) => (
              <div key={c.store_name} className="bg-ink p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-display text-xl text-gold w-6">{i + 1}</span>
                  <div className="min-w-0">
                    <p className="font-heading uppercase text-sm truncate">{c.store_name}</p>
                    <p className="text-muted-foreground text-xs font-mono">{c.orders} orders · {c.units} cases</p>
                  </div>
                </div>
                <span className="font-heading text-fire shrink-0">{money(c.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={`p-5 ${accent ? "bg-blaze/10" : "bg-ink"}`}>
      <Icon size={18} className={accent ? "text-blaze" : "text-gold"} />
      <p className="font-display text-3xl mt-3 leading-none">{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-2">{label}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-ink border border-surface-border p-4">
      <p className="font-display text-2xl leading-none">{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function StatusBar({ label, value, total, color }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[11px] uppercase text-muted-foreground w-20 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-surface-border"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
      <span className="font-mono text-xs w-6 text-right">{value}</span>
    </div>
  );
}

/* ---------------- Preorders tab ---------------- */
const STATUS_META = {
  pending: { label: "Pending", cls: "border-gold text-gold" },
  completed: { label: "Completed", cls: "border-green-500 text-green-400" },
  cancelled: { label: "Cancelled", cls: "border-muted-foreground text-muted-foreground" },
  void: { label: "Void", cls: "border-blaze text-blaze line-through" },
};

function Preorders({ authHeaders, token }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [retailers, setRetailers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ores, rres] = await Promise.all([
        fetch(`${API}/admin/orders`, { headers: authHeaders }),
        fetch(`${API}/retailers`),
      ]);
      setList(await ores.json());
      setRetailers(await rres.json().catch(() => []));
    } catch {
      toast.error("Failed to load preorders");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setStatus = async (order, status) => {
    if (status === "completed" && !order.added_to_locator) {
      if (!window.confirm(`Mark ${order.invoice_no} completed? "${order.store_name}" will be added to the public store locator.`)) return;
    }
    try {
      const res = await fetch(`${API}/admin/orders/${order.id}/status`, {
        method: "PATCH", headers: authHeaders, body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Update failed");
      toast.success(`${order.invoice_no} marked ${status}`);
      setList((l) => l.map((o) => (o.id === order.id ? data : o)));
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Permanently delete voided invoice ${order.invoice_no}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/orders/${order.id}`, { method: "DELETE", headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Delete failed");
      toast.success(`${order.invoice_no} deleted`);
      setList((l) => l.filter((o) => o.id !== order.id));
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/orders/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `hpg-preorders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const saveEdit = async ({ quantity, unit_price, edit_note }) => {
    const res = await fetch(`${API}/admin/orders/${editing.id}`, {
      method: "PATCH", headers: authHeaders,
      body: JSON.stringify({ quantity, unit_price, edit_note }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Could not update order");
    toast.success(`${editing.invoice_no} updated`);
    setList((l) => l.map((o) => (o.id === editing.id ? data : o)));
    setEditing(null);
  };

  const resendInvoice = async (order) => {
    if (!order.email) { toast.error("This order has no customer email on file."); return; }
    if (!window.confirm(`Email the current invoice ${order.invoice_no} to ${order.email}?`)) return;
    try {
      const res = await fetch(`${API}/admin/orders/${order.id}/resend`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ invoice_url: `${API}/orders/${order.id}/invoice.pdf` }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Send failed");
      toast.success(`Invoice ${order.invoice_no} emailed to ${data.to}`);
      if (data.order) setList((l) => l.map((o) => (o.id === order.id ? data.order : o)));
    } catch (err) {
      toast.error(err.message || "Send failed");
    }
  };

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };
  const filtered = filter === "all" ? list : list.filter((o) => o.status === filter);

  const createOrder = async ({ body, status }) => {
    const res = await fetch(`${API}/admin/orders?status=${encodeURIComponent(status)}`, {
      method: "POST", headers: authHeaders, body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Could not create order");
    toast.success(`Order ${data.order.invoice_no} created`);
    setAdding(false);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display uppercase text-3xl leading-none">Preorders</h2>
          <p className="text-muted-foreground text-sm mt-1">{list.length} total · completed orders auto-add to locator</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="add-order-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-blaze text-white font-heading uppercase text-xs tracking-wider py-3 px-5 hover:bg-gold hover:text-ink transition-colors">
            <Plus size={16} /> Add Preorder
          </button>
          <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-2 border border-surface-border font-heading uppercase text-xs tracking-wider py-3 px-5 hover:border-gold hover:text-gold transition-colors">
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {adding && <AdminOrderModal retailers={retailers} onClose={() => setAdding(false)} onSave={createOrder} />}
      {editing && <EditOrderModal order={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", "pending", "completed", "void"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${filter === f ? "border-blaze text-blaze" : "border-surface-border text-muted-foreground hover:text-white"}`}>
            {f}{f !== "all" ? ` (${list.filter((o) => o.status === f).length})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 p-12 text-muted-foreground border border-surface-border"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground border border-surface-border">No preorders here yet.</div>
      ) : (
        <div data-testid="preorders-list" className="space-y-px bg-surface-border border border-surface-border">
          {filtered.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.pending;
            return (
              <div key={o.id} data-testid={`preorder-${o.id}`} className="bg-ink p-5 hover:bg-surface transition-colors">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-sm text-gold">{o.invoice_no}</span>
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${meta.cls}`}>{meta.label}</span>
                      {o.added_to_locator && <span className="font-mono text-[10px] uppercase tracking-widest text-green-400 flex items-center gap-1"><MapPin size={11} /> On locator</span>}
                    </div>
                    <h3 className="font-heading uppercase text-base mt-2">{o.store_name}</h3>
                    <p className="text-muted-foreground text-sm mt-0.5">{o.address}, {o.city}, {o.state} {o.zip}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground font-mono mt-2">
                      <span>{o.poc_name}</span>
                      <span>{o.phone}</span>
                      <a href={`mailto:${o.email}`} className="text-blaze hover:text-gold">{o.email}</a>
                      <span>{fmt(o.created_at)}</span>
                    </div>
                    {o.notes && <p className="text-xs text-muted-foreground mt-2 italic">“{o.notes}”</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display text-2xl text-fire leading-none">{money(o.total)}</p>
                    <p className="font-mono text-[11px] text-muted-foreground mt-1">{o.quantity} × 25-Jar Case @ {money(o.unit_price)}</p>
                    {o.edit_history?.length > 0 && (
                      <span data-testid={`adjusted-badge-${o.id}`} className="inline-flex items-center gap-1 mt-1 font-mono text-[9px] uppercase tracking-widest text-gold border border-gold/40 px-1.5 py-0.5">
                        <Pencil size={9} /> Adjusted
                      </span>
                    )}
                  </div>
                </div>

                {o.edit_history?.length > 0 && (
                  <div data-testid={`edit-history-${o.id}`} className="mt-3 border-l-2 border-gold/40 pl-3 space-y-1">
                    {o.edit_history.slice(-3).map((h, idx) => (
                      <p key={idx} className="text-[11px] text-muted-foreground font-mono">
                        <span className="text-gold">{fmt(h.at)}</span> · {h.from.quantity}×{money(h.from.unit_price)} → {h.to.quantity}×{money(h.to.unit_price)} — “{h.note}”
                      </p>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-surface-border">
                  <a data-testid={`invoice-${o.id}`} href={`${API}/orders/${o.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-surface-border px-3 py-1.5 font-heading uppercase text-[11px] tracking-wider hover:border-white transition-colors">
                    <FileText size={13} /> Invoice
                  </a>
                  {o.status !== "void" && (
                    <button data-testid={`edit-${o.id}`} onClick={() => setEditing(o)} className="inline-flex items-center gap-1.5 border border-gold/50 text-gold px-3 py-1.5 font-heading uppercase text-[11px] tracking-wider hover:bg-gold hover:text-ink transition-colors">
                      <Pencil size={13} /> Edit
                    </button>
                  )}
                  {o.status !== "void" && o.email && (
                    <button data-testid={`resend-${o.id}`} onClick={() => resendInvoice(o)} className="inline-flex items-center gap-1.5 border border-surface-border px-3 py-1.5 font-heading uppercase text-[11px] tracking-wider hover:border-white transition-colors">
                      <Send size={13} /> Email Invoice
                    </button>
                  )}
                  {o.last_invoice_sent_at && (
                    <span data-testid={`sent-${o.id}`} className="font-mono text-[10px] text-green-400 flex items-center gap-1"><Send size={10} /> Sent {fmt(o.last_invoice_sent_at)}</span>
                  )}
                  <span className="flex-1" />
                  {o.status !== "completed" && (
                    <button data-testid={`complete-${o.id}`} onClick={() => setStatus(o, "completed")} className="border border-green-500/50 text-green-400 px-3 py-1.5 font-heading uppercase text-[11px] tracking-wider hover:bg-green-500 hover:text-ink transition-colors">Complete</button>
                  )}
                  {o.status !== "void" && (
                    <button data-testid={`void-${o.id}`} onClick={() => setStatus(o, "void")} className="border border-blaze/50 text-blaze px-3 py-1.5 font-heading uppercase text-[11px] tracking-wider hover:bg-blaze hover:text-white transition-colors">Void</button>
                  )}
                  {o.status === "void" && (
                    <button data-testid={`delete-${o.id}`} onClick={() => deleteOrder(o)} className="inline-flex items-center gap-1.5 border border-blaze/50 text-blaze px-3 py-1.5 font-heading uppercase text-[11px] tracking-wider hover:bg-blaze hover:text-white transition-colors"><Trash2 size={13} /> Delete</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminOrderModal({ retailers, onClose, onSave }) {
  const emptyForm = { store_name: "", poc_name: "", email: "", phone: "", address: "", city: "", state: "", zip: "", quantity: 1, notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [retailerId, setRetailerId] = useState("");
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isExisting = !!retailerId;

  const selectStore = async (e) => {
    const id = e.target.value;
    setRetailerId(id);
    if (!id) { setForm((f) => ({ ...f, store_name: "", address: "", city: "", state: "", zip: "" })); return; }
    const r = retailers.find((x) => x.id === id);
    if (r) setForm((f) => ({ ...f, store_name: r.name || "", address: r.address || "", city: r.city || "", state: r.state || "", zip: r.zip || "", phone: r.phone || f.phone }));
    try {
      const res = await fetch(`${API}/retailers/${id}/contact`);
      if (res.ok) { const c = await res.json(); setForm((f) => ({ ...f, poc_name: c.poc_name || f.poc_name, email: c.email || f.email })); }
    } catch {}
  };

  const ro = isExisting ? "opacity-70 cursor-not-allowed" : "";
  const total = (Number(form.quantity) || 0) * 40;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.store_name || !form.poc_name || !form.email || !form.phone || !form.address || !form.city || !form.state) {
      toast.error("Store, contact, email, phone, address, city and state are required.");
      return;
    }
    setBusy(true);
    try {
      await onSave({ body: { ...form, quantity: Number(form.quantity), retailer_id: retailerId || null }, status });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 backdrop-blur-sm px-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="admin-order-modal" className="w-full max-w-lg border border-surface-border bg-surface p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display uppercase text-2xl">Add Preorder</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-white" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {retailers.length > 0 && (
            <Field label="Existing store (auto-fill)">
              <select data-testid="ao-store-select" value={retailerId} onChange={selectStore} className={inputCls}>
                <option value="">— New store / not listed —</option>
                {retailers.map((r) => <option key={r.id} value={r.id}>{r.name} — {r.address}, {r.city} {r.state}</option>)}
              </select>
            </Field>
          )}
          <Field label="Store / Business Name *"><input data-testid="ao-store" value={form.store_name} onChange={set("store_name")} readOnly={isExisting} className={`${inputCls} ${ro}`} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Point of Contact *"><input data-testid="ao-poc" value={form.poc_name} onChange={set("poc_name")} className={inputCls} /></Field>
            <Field label="Phone *"><input data-testid="ao-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
          </div>
          <Field label="Email *"><input data-testid="ao-email" type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
          <Field label="Address *"><input data-testid="ao-address" value={form.address} onChange={set("address")} readOnly={isExisting} className={`${inputCls} ${ro}`} /></Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="City *"><input data-testid="ao-city" value={form.city} onChange={set("city")} readOnly={isExisting} className={`${inputCls} ${ro}`} /></Field>
            <Field label="State *"><input data-testid="ao-state" value={form.state} onChange={set("state")} readOnly={isExisting} className={`${inputCls} ${ro}`} /></Field>
            <Field label="ZIP"><input data-testid="ao-zip" value={form.zip} onChange={set("zip")} readOnly={isExisting} className={`${inputCls} ${ro}`} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="25-Jar Grabba Cases *"><input data-testid="ao-qty" type="number" min={1} value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value.replace(/[^0-9]/g, "") }))} onBlur={() => setForm((f) => ({ ...f, quantity: Math.max(1, Number(f.quantity) || 1) }))} className={inputCls} /></Field>
            <Field label="Status">
              <select data-testid="ao-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="pending">Pending</option>
                <option value="completed">Completed (sold/delivered)</option>
                <option value="void">Void</option>
              </select>
            </Field>
          </div>
          <Field label="Notes"><textarea data-testid="ao-notes" value={form.notes} onChange={set("notes")} rows={2} className={`${inputCls} resize-none`} /></Field>
          <p className="font-mono text-xs text-gold">Total: ${total.toFixed(2)} · $40.00 per box · COD</p>
        </div>
        <div className="flex gap-3 mt-8">
          <button data-testid="ao-save" type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-blaze text-white font-heading uppercase text-sm py-3.5 hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} Create Order
          </button>
          <button type="button" onClick={onClose} className="px-6 border border-surface-border font-heading uppercase text-sm hover:border-white transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function EditOrderModal({ order, onClose, onSave }) {
  const [quantity, setQuantity] = useState(String(order.quantity ?? 1));
  const [unitPrice, setUnitPrice] = useState(String(Number(order.unit_price ?? 40).toFixed(2)));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const qtyNum = Math.max(1, Number(quantity) || 0);
  const priceNum = Math.max(0, Number(unitPrice) || 0);
  const newTotal = qtyNum * priceNum;
  const changed = qtyNum !== order.quantity || priceNum !== Number(order.unit_price);

  const submit = async (e) => {
    e.preventDefault();
    if (!changed) { toast.error("Change the quantity or cost before saving."); return; }
    if (note.trim().length < 3) { toast.error("A note explaining the change is required."); return; }
    setBusy(true);
    try {
      await onSave({ quantity: qtyNum, unit_price: priceNum, edit_note: note.trim() });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/90 backdrop-blur-sm px-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-order-modal" className="w-full max-w-md border border-surface-border bg-surface p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display uppercase text-2xl">Adjust Invoice</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-white" aria-label="Close"><X size={20} /></button>
        </div>
        <p className="font-mono text-xs text-gold mb-6">{order.invoice_no} · {order.store_name}</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Number of Cases *">
              <input data-testid="edit-qty" type="number" min={1} value={quantity}
                onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, ""))}
                onBlur={() => setQuantity(String(Math.max(1, Number(quantity) || 1)))}
                className={inputCls} />
            </Field>
            <Field label="Cost per Case ($) *">
              <input data-testid="edit-price" type="number" min={0} step="0.01" value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                onBlur={() => setUnitPrice(String((Number(unitPrice) || 0).toFixed(2)))}
                className={inputCls} />
            </Field>
          </div>
          <div className="flex items-center justify-between border border-surface-border px-4 py-3 bg-black">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">New Total</span>
            <span data-testid="edit-new-total" className="font-display text-xl text-fire">${newTotal.toFixed(2)}</span>
          </div>
          <p className="font-mono text-[11px] text-muted-foreground">
            Was: {order.quantity} × ${Number(order.unit_price).toFixed(2)} = ${Number(order.total).toFixed(2)}
          </p>
          <Field label="Reason for change * (required)">
            <textarea data-testid="edit-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="e.g. Volume deal — dropped to $35/case for 20-case order"
              className={`${inputCls} resize-none`} />
          </Field>
        </div>
        <div className="flex gap-3 mt-8">
          <button data-testid="edit-save" type="submit" disabled={busy || note.trim().length < 3 || !changed}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-blaze text-white font-heading uppercase text-sm py-3.5 hover:bg-gold hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} Save Adjustment
          </button>
          <button type="button" onClick={onClose} className="px-6 border border-surface-border font-heading uppercase text-sm hover:border-white transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}


/* ---------------- Routes tab (delivery planning) ---------------- */
function Routes({ authHeaders }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/admin/orders`, { headers: authHeaders });
        setList(await res.json());
      } catch {
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const pending = list.filter((o) => o.status === "pending");

  // Group pending orders by city + state
  const groups = {};
  pending.forEach((o) => {
    const key = `${(o.city || "").trim()}, ${(o.state || "").trim().toUpperCase()}`;
    (groups[key] = groups[key] || []).push(o);
  });
  const routes = Object.entries(groups)
    .map(([area, orders]) => ({
      area, orders,
      stops: orders.length,
      cases: orders.reduce((s, o) => s + (o.quantity || 0), 0),
      value: orders.reduce((s, o) => s + (o.total || 0), 0),
    }))
    .sort((a, b) => b.stops - a.stops);

  const mapsUrl = (orders) => {
    const stops = orders.map((o) => encodeURIComponent(`${o.address}, ${o.city}, ${o.state} ${o.zip || ""}`.trim()));
    return `https://www.google.com/maps/dir/${stops.join("/")}`;
  };

  if (loading) return <div className="flex items-center gap-3 p-12 text-muted-foreground border border-surface-border"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

  return (
    <div data-testid="routes-panel">
      <h2 className="font-display uppercase text-3xl leading-none mb-1">Delivery Routes</h2>
      <p className="text-muted-foreground text-sm mb-6">Pending orders grouped by area · plan your stocking runs</p>

      <div className="grid grid-cols-3 gap-px bg-surface-border border border-surface-border mb-8">
        <MiniStat label="Pending stops" value={pending.length} />
        <MiniStat label="Cases to deliver" value={pending.reduce((s, o) => s + (o.quantity || 0), 0)} />
        <MiniStat label="COD to collect" value={money(pending.reduce((s, o) => s + (o.total || 0), 0))} />
      </div>

      {routes.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground border border-surface-border">No pending orders to route. New preorders will appear here grouped by city.</div>
      ) : (
        <div className="space-y-6">
          {routes.map((r) => (
            <div key={r.area} data-testid={`route-${r.area}`} className="border border-surface-border">
              <div className="flex flex-wrap items-center justify-between gap-3 p-5 bg-surface border-b border-surface-border">
                <div className="flex items-center gap-3">
                  <MapPin className="text-blaze" size={20} />
                  <div>
                    <h3 className="font-heading uppercase text-lg leading-none">{r.area}</h3>
                    <p className="text-muted-foreground text-xs font-mono mt-1">{r.stops} stop{r.stops !== 1 ? "s" : ""} · {r.cases} cases · {money(r.value)} COD</p>
                  </div>
                </div>
                <a
                  data-testid={`route-maps-${r.area}`}
                  href={mapsUrl(r.orders)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-blaze text-white font-heading uppercase text-[11px] tracking-wider py-2.5 px-4 hover:bg-gold hover:text-ink transition-colors"
                >
                  <Navigation size={14} /> Open Route in Maps
                </a>
              </div>
              <div className="space-y-px bg-surface-border">
                {r.orders.map((o, i) => (
                  <div key={o.id} className="bg-ink p-4 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="font-display text-lg text-gold w-6 shrink-0">{i + 1}</span>
                      <div className="min-w-0">
                        <p className="font-heading uppercase text-sm truncate">{o.store_name}</p>
                        <p className="text-muted-foreground text-sm">{o.address}, {o.city}, {o.state} {o.zip}</p>
                        <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground font-mono mt-1">
                          <span>{o.poc_name}</span>
                          <a href={`tel:${o.phone}`} className="text-blaze hover:text-gold">{o.phone}</a>
                          <span className="text-gold">{o.invoice_no}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-heading text-fire">{money(o.total)}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{o.quantity} × 25-Jar Case</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Reminders tab (reorder nudges) ---------------- */
function Reminders({ authHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(4);
  const [enabled, setEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/reminders`, { headers: authHeaders });
      const d = await res.json();
      setData(d);
      setWeeks(d.interval_weeks);
      setEnabled(d.enabled);
    } catch {
      toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: "PUT", headers: authHeaders,
        body: JSON.stringify({ reorder_interval_weeks: Number(weeks), reorder_reminders_enabled: enabled }),
      });
      if (!res.ok) throw new Error();
      toast.success("Reminder settings saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/admin/reminders/run`, { method: "POST", headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error();
      toast.success(d.sent > 0 ? `Sent ${d.sent} reorder reminder${d.sent > 1 ? "s" : ""}` : "No reminders were due");
      load();
    } catch {
      toast.error("Failed to send reminders");
    } finally {
      setRunning(false);
    }
  };

  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };
  const statusMeta = {
    due: { label: "Due now", cls: "border-blaze text-blaze" },
    reminded: { label: "Reminded", cls: "border-green-500 text-green-400" },
    active: { label: "Active", cls: "border-surface-border text-muted-foreground" },
  };
  const dueCount = data ? data.reminders.filter((r) => r.status === "due").length : 0;

  return (
    <div data-testid="reminders-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display uppercase text-3xl leading-none">Reorder Reminders</h2>
          <p className="text-muted-foreground text-sm mt-1">Auto-nudge stores to restock after a set time</p>
        </div>
        <button data-testid="run-reminders-btn" onClick={runNow} disabled={running} className="inline-flex items-center gap-2 bg-blaze text-white font-heading uppercase text-xs tracking-wider py-3 px-5 hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
          {running ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Send Due Now {dueCount > 0 ? `(${dueCount})` : ""}
        </button>
      </div>

      {/* Settings card */}
      <div className="border border-surface-border p-6 mb-8">
        <h3 className="font-heading uppercase text-sm tracking-wider mb-4 flex items-center gap-2"><Clock size={16} /> Settings</h3>
        <div className="flex flex-wrap items-end gap-6">
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">Remind after (weeks)</span>
            <input data-testid="reminder-weeks" type="number" min={1} max={52} value={weeks} onChange={(e) => setWeeks(e.target.value.replace(/[^0-9]/g, ""))} className={`${inputCls} w-32`} />
          </label>
          <label className="flex items-center gap-3 cursor-pointer pb-3">
            <input data-testid="reminder-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-5 h-5 accent-blaze" />
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Auto reminders {enabled ? "on" : "off"}</span>
          </label>
          <button data-testid="save-reminder-settings" onClick={saveSettings} disabled={savingSettings} className="inline-flex items-center gap-2 border border-surface-border font-heading uppercase text-xs tracking-wider py-3 px-5 hover:border-gold hover:text-gold transition-colors disabled:opacity-60">
            {savingSettings ? <Loader2 className="animate-spin" size={15} /> : null} Save
          </button>
        </div>
        <p className="text-muted-foreground/70 text-xs mt-4">Stores get one friendly email when it's been {weeks} weeks since their last delivered order. They become eligible again after their next order.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 p-12 text-muted-foreground border border-surface-border"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : !data || data.reminders.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground border border-surface-border">No stores tracked yet. Stores are added here once you mark their first order as completed.</div>
      ) : (
        <div data-testid="reminders-list" className="space-y-px bg-surface-border border border-surface-border">
          {data.reminders.map((r) => {
            const meta = statusMeta[r.status] || statusMeta.active;
            return (
              <div key={r.email} data-testid={`reminder-${r.email}`} className="bg-ink p-5 flex flex-wrap items-center justify-between gap-3 hover:bg-surface transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading uppercase text-sm truncate">{r.store_name}</h3>
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 text-xs text-muted-foreground font-mono mt-1.5">
                    <a href={`mailto:${r.email}`} className="text-blaze hover:text-gold">{r.email}</a>
                    <span>Last order: {fmt(r.last_order_at)} ({r.days_since_order}d ago)</span>
                    <span>Last reminded: {fmt(r.last_reminder_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- shared ---------------- */
const inputCls = "w-full bg-black border border-surface-border text-white px-4 py-3 text-sm focus:outline-none focus:border-blaze focus:ring-1 focus:ring-blaze transition-colors placeholder:text-muted-foreground/50";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">{label}</span>
      {children}
    </label>
  );
}
